/* Hairmosa — en-tête de page de collection : bandeau vidéo.
   Custom element <hm-collection-entete> : connectedCallback se relance seul quand
   l'éditeur remplace le HTML de la section.

   Le <video muted loop playsinline preload="none"> n'a pas de <source> au chargement.
   Au premier affichage du bandeau (IntersectionObserver), on choisit la vidéo (mobile
   ou desktop, selon la largeur) puis, parmi ses rendus MP4, le plus léger — plafonné à
   720p sous 900px. Le poster et le rapport de forme sont ajustés dès la connexion,
   indépendamment de la visibilité. Lecture coupée si prefers-reduced-motion. */
(() => {
  'use strict';
  if (window.customElements.get('hm-collection-entete')) return;

  const ICONE_PAUSE = '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2.5 1.5h2.5v9H2.5zM7 1.5h2.5v9H7z"/></svg>';
  const ICONE_LECTURE = '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M3 1.5v9L10.5 6z"/></svg>';
  const num = (v) => parseFloat(v) || 0;

  /* Choisit, parmi les sources MP4 fournies par Shopify (video.sources), la plus légère dont la
     hauteur couvre la hauteur affichée × densité de pixels — plafonnée à 720p sous 900px de large
     (jamais de 1080p sur mobile). Sans correspondance : la plus grande disponible sous ce plafond. */
  function choisirSourceMp4(sources, hauteurAffichee) {
    let mp4 = (sources || []).filter((s) => /mp4/i.test(s.type || '') && num(s.height) > 0);
    if (!mp4.length) return null;
    if (window.innerWidth < 900) {
      const plafonnees = mp4.filter((s) => num(s.height) <= 720);
      if (plafonnees.length) mp4 = plafonnees;
    }
    mp4.sort((a, b) => num(a.height) - num(b.height));
    const cible = hauteurAffichee * (window.devicePixelRatio || 1);
    return mp4.find((s) => num(s.height) >= cible) || mp4[mp4.length - 1];
  }

  class HmCollectionEntete extends HTMLElement {
    connectedCallback() {
      this.media = this.querySelector('[data-hm-hero-media]');
      this.video = this.querySelector('[data-hm-hero-video]');
      this.pauseBtn = this.querySelector('[data-hm-hero-pause]');
      if (!this.media || !this.video) return;

      this.abort = new AbortController();
      this.desktop = window.matchMedia('(min-width: 900px)');
      this.sobreMql = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.sobre = this.sobreMql.matches;
      this.demarree = false;
      this.enLecture = false;
      this.enPause = this.sobre;
      this.visible = false;

      this.ajusterFormat();
      this.synchroniserBouton();

      const opts = { signal: this.abort.signal };
      this.desktop.addEventListener('change', () => this.ajusterFormat(), opts);
      this.sobreMql.addEventListener('change', (ev) => { this.sobre = ev.matches; this.evaluer(); }, opts);
      if (this.pauseBtn) {
        this.pauseBtn.addEventListener('click', () => { this.enPause = !this.enPause; this.evaluer(); }, opts);
      }

      this.lierObservateur();
    }

    disconnectedCallback() {
      if (this.observateur) this.observateur.disconnect();
      if (this.abort) this.abort.abort();
    }

    ajusterFormat() {
      const mobile = !this.desktop.matches;
      const poster = mobile ? this.media.dataset.hmHeroPosterMobile : this.media.dataset.hmHeroPosterDesktop;
      const ratio = mobile ? this.media.dataset.hmHeroRatioMobile : this.media.dataset.hmHeroRatioDesktop;
      if (poster) this.video.setAttribute('poster', poster);
      if (ratio) this.media.style.aspectRatio = ratio;
    }

    lierObservateur() {
      if (!('IntersectionObserver' in window)) {
        this.visible = true;
        this.evaluer();
        return;
      }
      this.observateur = new IntersectionObserver((entrees) => {
        entrees.forEach((entree) => { this.visible = entree.isIntersecting; });
        this.evaluer();
      }, { threshold: .5 });
      this.observateur.observe(this.media);
    }

    evaluer() {
      const eligible = this.visible && !this.sobre && !this.enPause;
      if (eligible) this.lire(); else this.mettreEnPause();
    }

    lire() {
      if (!this.demarree) {
        const mobile = !this.desktop.matches;
        let sources = [];
        try {
          sources = JSON.parse((mobile ? this.video.dataset.hmHeroSourcesMobile : this.video.dataset.hmHeroSourcesDesktop) || '[]');
        } catch (e) { sources = []; }
        const rect = this.video.getBoundingClientRect();
        const choisie = choisirSourceMp4(sources, rect.height || this.video.clientHeight);
        if (choisie) {
          const source = document.createElement('source');
          source.src = choisie.url;
          if (choisie.type) source.type = choisie.type;
          this.video.appendChild(source);
          this.video.load();
        }
        this.demarree = true;
      }
      const p = this.video.play();
      if (p && p.catch) p.catch(() => { /* lecture refusée : le poster reste affiché */ });
      this.enLecture = true;
      this.synchroniserBouton();
    }

    mettreEnPause() {
      this.video.pause();
      this.enLecture = false;
      this.synchroniserBouton();
    }

    synchroniserBouton() {
      if (!this.pauseBtn) return;
      this.pauseBtn.innerHTML = this.enLecture ? ICONE_PAUSE : ICONE_LECTURE;
      this.pauseBtn.setAttribute('aria-label', this.enLecture ? this.dataset.hmHeroPauseLabel : this.dataset.hmHeroLectureLabel);
    }
  }

  window.customElements.define('hm-collection-entete', HmCollectionEntete);
})();
